import { Link } from 'react-router-dom';
import { text, table, cx } from '../../styles/theme';
import { SectionCard } from '../SectionCard';
import { formatTimestamp, formatUsd } from '../../utils/format';
import { modeLabel, modeChipClass, sincePrompt } from '../../utils/mode';
import type { SessionSummary } from '../../types/api';

const MS_PER_HOUR = 3600 * 1000;

// State thresholds: recently touched = Active; quiet for days gets a
// descriptive "Quiet Nd" chip — long-running compacted sessions are often
// dormant-but-alive, so silence is not a problem state (Needs a Look flags
// the ones with actual friction signals); in between carries no badge.
const activeWithin = 6 * 3600 * 1000;
const quietAfter = 48 * 3600 * 1000;
const MS_PER_DAY = 24 * 3600 * 1000;

type SessionState = { kind: 'active' } | { kind: 'quiet'; days: number } | null;

function sessionState(lastActiveMs: number): SessionState {
  const quiet = Date.now() - lastActiveMs;
  if (quiet < activeWithin) return { kind: 'active' };
  if (quiet >= quietAfter) return { kind: 'quiet', days: Math.floor(quiet / MS_PER_DAY) };
  return null;
}

function agentHours(s: SessionSummary): number {
  const duration = parseInt(s.durationMs || '0', 10);
  const idle = parseInt(String((s as { timeBreakdown?: { idleMs?: string | number } }).timeBreakdown?.idleMs ?? '0'), 10);
  return Math.max(0, duration - idle) / MS_PER_HOUR;
}

function lastActiveMs(s: SessionSummary): number {
  return parseInt(s.startedAt || '0', 10) * 1000 + parseInt(s.durationMs || '0', 10);
}

/**
 * LatestSessionsTable is the Home footer: the most recent sessions with
 * their working time, priced value, and liveness state.
 */
export function LatestSessionsTable({ sessions }: { sessions: SessionSummary[] }) {
  if (sessions.length === 0) return null;

  return (
    <SectionCard
      title="Latest Sessions"
      flush
      action={
        <Link to="/sessions" className={cx('text-sm font-medium', text.link)}>
          View all →
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className={cx('min-w-full', table.divide)}>
          <thead>
            <tr>
              {['Project', 'Last active', 'Turns', 'Agent time', 'Value', 'State'].map((h, i) => (
                <th
                  key={h}
                  className={cx(
                    'px-4 py-2.5 text-xs font-medium uppercase',
                    i >= 2 && i <= 4 ? 'text-right' : 'text-left',
                    table.headerText,
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={table.divideMuted}>
            {sessions.map((s) => {
              const last = lastActiveMs(s);
              const state = sessionState(last);
              const hours = agentHours(s);
              const cost = (s as { costUsd?: number }).costUsd ?? 0;
              return (
                <tr key={s.sessionId} className={table.rowHover}>
                  <td className="px-4 py-3">
                    <Link to={`/sessions/${s.sessionId}`} className={cx('font-medium hover:underline', text.primary)}>
                      {s.project || 'unknown'}
                    </Link>
                    <div className={cx('font-mono text-xs', text.muted)}>{s.sessionId.slice(0, 8)}</div>
                  </td>
                  <td className={cx('px-4 py-3 text-sm whitespace-nowrap', text.secondary)}>
                    {formatTimestamp(String(Math.floor(last / 1000)))}
                  </td>
                  <td className={cx('px-4 py-3 text-right font-mono text-sm tabular-nums', text.primary)}>
                    {s.totalTurns}
                  </td>
                  <td className={cx('px-4 py-3 text-right font-mono text-sm tabular-nums', text.primary)}>
                    {hours >= 0.05 ? `${hours.toFixed(1)}h` : '—'}
                  </td>
                  <td className={cx('px-4 py-3 text-right font-mono text-sm tabular-nums', text.primary)}>
                    {cost > 0 ? formatUsd(cost) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {state?.kind === 'active' && (() => {
                      // Live badge: the final sitting's interaction mode, with
                      // an "Nm since prompt" suffix for autonomous runs so a
                      // cruising session reads as cruising, not idle.
                      const label = modeLabel(s.currentMode);
                      const since = s.currentMode === 'autonomous' ? sincePrompt(s.lastPromptAt) : null;
                      return (
                        <span className={cx('rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap', modeChipClass(label ? s.currentMode : undefined))}>
                          {label ?? 'Active'}
                          {since && <span className="tabular-nums"> · {since} since prompt</span>}
                        </span>
                      );
                    })()}
                    {state?.kind === 'quiet' && (
                      <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600 dark:border-navy-700 dark:bg-navy-800 dark:text-navy-300">
                        Quiet {state.days}d
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
